import { Component, OnInit, OnDestroy, Inject, ViewChild, ElementRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DrupalRESTService } from '../../../services/drupal-rest.service';
import { AuthService } from '../../../services/auth.service';
import { PermissionsService } from '../../../services/permissions.service';
import { Activity } from '../activity-feed/activity-feed.component';
import { LoggerService } from '../../../services/logger.service';

export interface ActivityHistoryData {
  studioId: string;
}

@Component({
  selector: 'app-activity-history-dialog',
  templateUrl: './activity-history-dialog.component.html',
  styleUrls: ['./activity-history-dialog.component.css']
})
export class ActivityHistoryDialogComponent implements OnInit, OnDestroy {
  @ViewChild('scrollContainer') scrollContainer: ElementRef;
  
  activities: Activity[] = [];
  filteredActivities: Activity[] = [];
  loading: boolean = true;
  loadingMore: boolean = false;
  error: string = '';
  hasMore: boolean = true;
  
  // Pagination
  currentPage: number = 0;
  itemsPerPage: number = 20;
  
  // Filter options
  selectedType: string = 'all';
  activityTypes = [
    { value: 'all', label: 'All Activities' },
    { value: 'lesson', label: 'Classes' },
    { value: 'enrollment', label: 'Enrollments' },
    { value: 'payment', label: 'Payments' },
    { value: 'student', label: 'Students' }
  ];

  private scrollListener: any;

  constructor(
    public dialogRef: MatDialogRef<ActivityHistoryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private drupalRESTService: DrupalRESTService,
    private authService: AuthService,
    private permissionsService: PermissionsService
  ) {}

  ngOnInit(): void {
    this.activityTypes = this.activityTypes.filter(type => type.value === 'all' || this.canViewActivityType(type.value));
    this.loadAllActivity();
  }

  ngAfterViewInit(): void {
    // Set up scroll listener for endless scrolling
    if (this.scrollContainer) {
      this.setupScrollListener();
    }
  }

  ngOnDestroy(): void {
    if (this.scrollListener && this.scrollContainer) {
      this.scrollContainer.nativeElement.removeEventListener('scroll', this.scrollListener);
    }
  }

  private setupScrollListener(): void {
    const element = this.scrollContainer?.nativeElement;
    if (!element) return;

    this.scrollListener = () => {
      const threshold = 200; // Load more when within 200px of bottom
      const position = element.scrollTop + element.offsetHeight;
      const height = element.scrollHeight;
      
      if (position > height - threshold && !this.loadingMore && this.hasMore) {
        this.loadMore();
      }
    };

    element.addEventListener('scroll', this.scrollListener);
  }

  private loadAllActivity(reset: boolean = true): void {
    const studioId = this.data?.studioId || this.authService.selectedStudio?.id;
    
    if (!studioId) {
      this.error = 'No studio selected';
      this.loading = false;
      return;
    }

    if (reset) {
      this.currentPage = 0;
      this.activities = [];
      this.loading = true;
    }
    
    this.error = '';
    
    // Use pagination parameters
    const endpoint = `/amt_dashboard/recent-activity/${studioId}?page=${this.currentPage}&limit=${this.itemsPerPage}`;
    
    this.drupalRESTService.httpGET(endpoint).subscribe({
      next: (response: any) => {
        if (response && response.success && response.data) {
          if (reset) {
            this.activities = response.data;
          } else {
            // Append for endless scroll
            this.activities = [...this.activities, ...response.data];
          }
          
          // Check if there are more items
          this.hasMore = response.has_more !== false && response.data.length === this.itemsPerPage;
          
          this.applyFilter();
        } else {
          if (reset) {
            this.activities = [];
            this.filteredActivities = [];
          }
          this.hasMore = false;
        }
        this.loading = false;
        this.loadingMore = false;
      },
      error: (error) => {
        LoggerService.error('Error loading activity history:', error);
        this.error = 'Failed to load activity history';
        if (reset) {
          this.activities = [];
          this.filteredActivities = [];
        }
        this.loading = false;
        this.loadingMore = false;
        this.hasMore = false;
      }
    });
  }

  applyFilter(): void {
    const visibleActivities = this.activities.filter(activity => this.canViewActivityType(activity.type));
    if (this.selectedType === 'all') {
      this.filteredActivities = visibleActivities;
      return;
    }

    if (!this.canViewActivityType(this.selectedType)) {
      this.filteredActivities = [];
      return;
    }

    this.filteredActivities = visibleActivities.filter(activity => activity.type === this.selectedType);
  }

  onFilterChange(): void {
    // When filter changes, reset and reload
    this.currentPage = 0;
    this.hasMore = true;
    
    if (this.selectedType === 'all') {
      // If switching to "all", we need to reload from server
      this.loadAllActivity(true);
    } else {
      // For specific filters, just filter existing data
      this.applyFilter();
    }
  }

  getFilteredActivities(): Activity[] {
    return this.filteredActivities;
  }

  loadMore(): void {
    if (this.loadingMore || !this.hasMore) return;
    
    this.loadingMore = true;
    this.currentPage++;
    
    // Load next page from API
    this.loadAllActivity(false);
  }

  getIcon(type: string): string {
    const iconMap = {
      'enrollment': 'school',
      'payment': 'payment',
      'lesson': 'event',
      'student': 'person_add',
      'system': 'info'
    };
    return iconMap[type] || 'info';
  }

  getIconColorClass(type: string): string {
    const colorMap = {
      'enrollment': 'icon-primary',
      'payment': 'icon-success',
      'lesson': 'icon-accent',
      'student': 'icon-info',
      'system': 'icon-warning'
    };
    return colorMap[type] || 'icon-default';
  }

  refresh(): void {
    this.loadAllActivity(true);
  }

  close(): void {
    this.dialogRef.close();
  }

  private canViewActivityType(type: string): boolean {
    switch (type) {
      case 'student':
        return this.permissionsService.canAccess('students_view');
      case 'enrollment':
        return this.permissionsService.canAccess('enrollments_view');
      case 'payment':
        return this.permissionsService.canAccess('payments_view');
      case 'lesson':
        return this.permissionsService.canAccess('calendar_view');
      case 'all':
      case 'system':
      default:
        return true;
    }
  }
}
